import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { skipToken } from "@reduxjs/toolkit/query";
import { HttpStatusCode } from "axios";
import { getApiErrorStatus, getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router";
import { useGetTemplateUsagesQuery } from "work/template-usages/_api/template-usage-api";
import { TemplateUsageKind } from "work/template-usages/_schemas/template-usage-schema";
import TemplateUsage from "work/template-usages/template-usage";
import { shellTopBannersHeight } from "_shared/_views/shell-top-banners";

/**
 * The application's header is drawn above Material's drawer layer (`zIndex.drawer + 1`), so a paper
 * that starts at the window's top edge has its title and close button under the header. The paper
 * starts below the header instead, at the header's own heights: `spacing(8)`, and `spacing(7)` below
 * `sm` (`headers/header.tsx`).
 */
const TemplateDrawer = styled(Drawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    height: `calc(100% - ${theme.spacing(8)} - ${shellTopBannersHeight()})`,
    top: `calc(${theme.spacing(8)} + ${shellTopBannersHeight()})`,
    [theme.breakpoints.down("sm")]: {
      height: `calc(100% - ${theme.spacing(7)} - ${shellTopBannersHeight()})`,
      top: `calc(${theme.spacing(7)} + ${shellTopBannersHeight()})`,
    },
  },
}));

const DrawerContent = styled("aside")(({ theme }) => ({
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  maxWidth: "100vw",
  padding: theme.spacing(2),
  width: "440px",
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  alignItems: "flex-start",
  display: "flex",
  gap: theme.spacing(1),
  justifyContent: "space-between",
}));

const DrawerTitle = styled(Typography)(() => ({
  overflowWrap: "anywhere",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const GroupTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginTop: theme.spacing(1),
}));

const UsageItem = styled(ListItem)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  justifyContent: "space-between",
  paddingLeft: 0,
  paddingRight: 0,
}));

type TemplateDetailsDrawerProps = {
  /** The template the panel describes; nothing is shown or asked for while it is undefined. */
  templateId?: string;
  kind: TemplateUsageKind;
  title: string;
  /** What the template holds, as the page that lists it would summarize it. */
  summary: ReactNode;
  /** Where the template itself opens, when the reader may go there. */
  templateHref?: string;
  onClose: () => void;
};

/**
 * A template's details, and the proposals and work built from it, grouped by who they are with
 * (#2401).
 *
 * Shared by the team and fee schedule template pages, which differ only in the summary they hand
 * it. Before this, nothing said whether a template was in use before someone archived or deleted it.
 */
export default function TemplateDetailsDrawer(props: Readonly<TemplateDetailsDrawerProps>) {
  const { templateId, kind, title, summary, templateHref, onClose } = props;
  const open = Boolean(templateId);

  return (
    <TemplateDrawer anchor="right" open={open} onClose={onClose}>
      <DrawerContent aria-label={`${title} details`}>
        <DrawerHeader>
          <DrawerTitle variant="h5">{title}</DrawerTitle>
          <Tooltip title="Close panel">
            <IconButton aria-label="Close template details" onClick={onClose}>
              <KeyboardTabIcon />
            </IconButton>
          </Tooltip>
        </DrawerHeader>
        <Box>{summary}</Box>
        {templateHref && (
          <Box>
            <Button component={RouterLink} to={templateHref} variant="outlined" size="small">
              Open template
            </Button>
          </Box>
        )}
        <Divider />
        <section aria-labelledby="template-used-by-title">
          <SectionTitle id="template-used-by-title" variant="h6">Used by</SectionTitle>
          {templateId && <TemplateUsageList kind={kind} templateId={templateId} />}
        </section>
      </DrawerContent>
    </TemplateDrawer>
  );
}

function TemplateUsageList(props: Readonly<{ kind: TemplateUsageKind; templateId: string }>) {
  const { kind, templateId } = props;
  const usagesQuery = useGetTemplateUsagesQuery(
    templateId ? { kind, templateId } : skipToken,
    // Proposals built from the template come and go while the page stays open, and the panel is
    // opened on purpose to find out; a cached answer from earlier would defeat it.
    { refetchOnMountOrArgChange: true },
  );

  if (usagesQuery.isLoading) {
    return (
      <Box aria-busy="true" aria-label="Loading where this template is used">
        <Skeleton height={32} />
        <Skeleton height={32} />
      </Box>
    );
  }

  if (usagesQuery.isError) {
    const refused = getApiErrorStatus(usagesQuery.error) === HttpStatusCode.Forbidden;
    return (
      <Alert
        severity={refused ? "warning" : "error"}
        action={refused ? undefined : <Button color="inherit" onClick={() => void usagesQuery.refetch()}>Try again</Button>}
      >
        {refused
          ? "You do not have permission to see where this template is used."
          : getErrorMessageFromUnknown(usagesQuery.error) ?? "Could not load where this template is used."}
      </Alert>
    );
  }

  const usages = (usagesQuery.data ?? []).map(TemplateUsage.fromJSON);
  if (usages.length === 0) {
    return (
      <Typography color="text.secondary">
        Not used yet. No proposal or work you can see was built from this template.
      </Typography>
    );
  }

  return (
    <Box>
      {TemplateUsage.groupByCounterparty(usages).map((group) => (
        <Box key={group.key} component="section" aria-label={group.counterparty}>
          <GroupTitle variant="subtitle2">{group.counterparty}</GroupTitle>
          <List dense disablePadding>
            {group.usages.map((usage) => (
              <UsageItem key={usage.workAgreementId}>
                <Link component={RouterLink} to={usage.href} sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                  {usage.name}
                </Link>
                <Chip label={usage.stageLabel} size="small" variant="outlined" />
              </UsageItem>
            ))}
          </List>
        </Box>
      ))}
    </Box>
  );
}
